import React, { createContext, useContext, useState, useEffect } from 'react';
import enTranslations from '../i18n/en.json';
import arTranslations from '../i18n/ar.json';
import { LANG_PARAM, languageFromSearch } from '../utils/seo/meta';

const LanguageContext = createContext();

const translations = {
  en: enTranslations,
  ar: arTranslations
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within LanguageProvider');
  }
  return context;
};

/** The language context, or undefined outside a LanguageProvider (no throw). */
export const useOptionalLanguage = () => useContext(LanguageContext);

export const LanguageProvider = ({ children }) => {
  // `?lang=ar` / `?lang=en` in the URL wins (the Arabic URL crawlers and
  // shared links use, see utils/seo/meta.js), then the saved choice.
  const [language, setLanguage] = useState(() => {
    const fromUrl = typeof window !== 'undefined' ? languageFromSearch(window.location.search) : null;
    if (fromUrl) return fromUrl;
    try { return window.localStorage.getItem('language') || 'en'; } catch { return 'en'; }
  });

  useEffect(() => {
    try { window.localStorage.setItem('language', language); } catch { /* storage blocked */ }
    // A URL that names a language follows a switch, so it never shows one
    // language while saying the other (English = no parameter).
    try {
      const url = new URL(window.location.href);
      const named = url.searchParams.get(LANG_PARAM);
      if (named && named !== language) {
        if (language === 'ar') url.searchParams.set(LANG_PARAM, 'ar');
        else url.searchParams.delete(LANG_PARAM);
        window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
      }
    } catch { /* no URL API: leave the address as it is */ }
    // The only writer of <html lang/dir> (SEOHead leaves them alone), and
    // only on a real change: each change restyles the whole page. The tag
    // keeps the region (en-AE / ar-AE), as the head manager's predecessor
    // wrote it; code that only needs the language reads the first subtag.
    const root = document.documentElement;
    const dir = language === 'ar' ? 'rtl' : 'ltr';
    const tag = language === 'ar' ? 'ar-AE' : 'en-AE';
    if (root.dir !== dir) root.dir = dir;
    if (root.lang !== tag) root.lang = tag;
    // The skip link lives in public/index.html (outside React).
    const skip = document.querySelector('.skip-to-content');
    const skipText = language === 'ar' ? 'انتقل إلى المحتوى الرئيسي' : 'Skip to main content';
    if (skip && skip.textContent !== skipText) skip.textContent = skipText;
    
    // Add RTL class to body for Tailwind
    // (toggle with a force writes the attribute only when it changes).
    document.body.classList.toggle('rtl', dir === 'rtl');
    document.body.classList.toggle('ltr', dir === 'ltr');
  }, [language]);

  const toggleLanguage = () => {
    setLanguage(prev => prev === 'en' ? 'ar' : 'en');
  };

  const t = (key) => {
    // Support object format {en: 'text', ar: 'نص'}
    if (typeof key === 'object' && key !== null) {
      return key[language] || key['en'] || '';
    }
    
    // Support key format 'common.loading'
    if (typeof key === 'string') {
      const keys = key.split('.');
      let value = translations[language];
      
      for (const k of keys) {
        if (value && typeof value === 'object') {
          value = value[k];
        } else {
          return key;
        }
      }
      
      return value || key;
    }
    
    return key;
  };

  return (
    <LanguageContext.Provider value={{ 
      language, 
      lang: language,
      setLanguage, 
      toggleLanguage, 
      t,
      dir: language === 'ar' ? 'rtl' : 'ltr',
      isRTL: language === 'ar',
      isArabic: language === 'ar',
      isEnglish: language === 'en'
    }}>
      {children}
    </LanguageContext.Provider>
  );
};

export default LanguageContext;
